/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { MdCheckCircle, MdCancel, MdOutlineError } from "react-icons/md";
import { format } from "date-fns";
import { configConstants } from "constants/configCategory";
import Actions from "../../../../../components/actions/index";
import { useGlobalContext } from "context/globalContext";
import Loader from "components/loader/Loader";
import { getFilterConfigsAPI, getConfigAPI } from "Api/config";
import FilterDropdown from "../../new-system-prompts/components/filter-dropdown/FilterDropdown";
import Card from "components/card";
import ConfigMenu from "components/card/ConfigMenu";

const ConfigTable = () => {
  const { fetchConfig, setFetchConfig, filterData, getFilterdata } =
    useGlobalContext();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterTableData, setFilterTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [totalPages, setTotalPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);

  const debounceSearch = useMemo(() => {
    let timeoutId;
    return function (value) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchQuery(value);
        setPageIndex(0);
      }, 300);
    };
  }, []);

  const selectedFilterArray = Object?.keys(selectedFilters)?.filter(
    (key) => selectedFilters[key] !== ""
  );

  const getFilterConfigs = useCallback(async () => {
    if (!searchQuery && selectedFilterArray?.length === 0) return;
    let payload;
    if (searchQuery && selectedFilterArray?.length > 0) {
      const filterObj = Object?.keys(selectedFilters)
        ?.filter((key) => selectedFilters[key] !== "")
        ?.reduce((acc, key) => ({ ...acc, [key]: selectedFilters[key] }), {});
      payload = filterObj?.status
        ? {
            searchQuery,
            ...{ ...filterObj, status: filterObj?.status === "Active" },
          }
        : {
            searchQuery,
            ...filterObj,
          };
    }
    if (searchQuery && selectedFilterArray?.length === 0)
      payload = { searchQuery };
    if (!searchQuery && selectedFilterArray?.length > 0) {
      const filterObj = Object?.keys(selectedFilters)
        ?.filter((key) => selectedFilters[key] !== "")
        ?.reduce((acc, key) => ({ ...acc, [key]: selectedFilters[key] }), {});
      payload = filterObj?.status
        ? { ...{ ...filterObj, status: filterObj?.status === "Active" } }
        : {
            ...filterObj,
          };
    }
    const token = localStorage.getItem("jwtToken");

    const skip = pageIndex * 10;
    const limit = 10;
    try {
      const response = await getFilterConfigsAPI(
        { searchQuery, pageIndex, limit, skip },
        payload,
        setIsLoading,
        token
      );
      if (response) {
        setFilterTableData(response.data);
        setTotalPages(response.totalPages);
        setFetchConfig(false);
      } else {
        setFilterTableData([]);
      }
    } catch (error) {
      console.error("Error fetching filtered config:", error);
    }
  }, [
    searchQuery,
    pageIndex,
    selectedFilterArray?.length,
    selectedFilters,
    setFetchConfig,
  ]);

  const getConfig = useCallback(async () => {
    if (!searchQuery && selectedFilterArray.length === 0) {
      const token = localStorage.getItem("jwtToken");
      const skip = 10;

      try {
        setIsLoading(true);
        const response = await getConfigAPI(
          {
            skip,
            pageIndex,
          },
          setIsLoading,
          token
        );
        if (response && response.data) {
          setTableData(response.data);
          setTotalPages(response?.pagination?.totalPages || 0);
          setFetchConfig(false);
        } else {
          setTableData([]);
        }
      } catch (error) {
        console.error("Error fetching destinations:", error);
      }
    }
  }, [searchQuery, selectedFilterArray?.length, setFetchConfig, pageIndex]);

  useEffect(() => {
    getFilterConfigs(pageIndex);
  }, [searchQuery, selectedFilters, pageIndex, getFilterConfigs]);

  useEffect(() => {
    if (fetchConfig) {
      getConfig();
      getFilterdata();
    }
    setFetchConfig(false);
  }, [fetchConfig, getConfig, setFetchConfig, getFilterdata]);

  useEffect(() => {
    if (searchQuery || selectedFilterArray.length > 0) {
      getFilterConfigs();
    }
  }, [searchQuery, selectedFilters, pageIndex, getFilterConfigs]);

  useEffect(() => {
    if (!searchQuery && selectedFilterArray.length === 0) {
      getConfig();
    }
  }, [
    fetchConfig,
    pageIndex,
    searchQuery,
    selectedFilterArray.length,
    getConfig,
  ]);
  useEffect(() => {
    if (fetchConfig) {
      if (searchQuery || selectedFilterArray.length > 0) {
        getFilterConfigs();
      } else {
        getConfig();
      }
      getFilterdata();
      setFetchConfig(false);
    }
  }, [
    fetchConfig,
    getConfig,
    getFilterConfigs,
    searchQuery,
    selectedFilterArray.length,
  ]);

  const columnsData = useMemo(
    () => [
      { Header: "Key", accessor: "key" },
      { Header: "Value", accessor: "value" },
      { Header: "Status", accessor: "status" },
      { Header: "Comment", accessor: "description" },
      { Header: "Created At", accessor: "createdAt" },
      {
        Header: "Config",
        accessor: "configCategory",
        Cell: ({ value }) => {
          const config = configConstants.find((item) => item.value === value);
          return config ? config.type : value;
        },
      },
      { Header: "Actions", accessor: "actions" },
    ],
    []
  );

  const columns = useMemo(() => columnsData, [columnsData]);

  const tableInstance = useTable(
    {
      columns,
      data:
        searchQuery || selectedFilterArray.length > 0
          ? filterTableData
          : tableData,

      manualPagination: true,
      pageCount: totalPages,
      pageIndex,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    gotoPage,
  } = tableInstance;

  const filters = filterData?.find(
    (filter) => filter?.table === "config"
  )?.filters;

  const handlePageChange = (direction) => {
    if (direction === "next" && pageIndex < totalPages - 1) {
      const nextIndex = pageIndex + 1;
      setPageIndex(nextIndex);
      gotoPage(nextIndex);
    } else if (direction === "previous" && pageIndex > 0) {
      const prevIndex = pageIndex - 1;
      setPageIndex(prevIndex);
      gotoPage(prevIndex);
    }
  };

  return (
    <Card extra={"w-full h-full p-4 sm:overflow-x-auto"}>
      <div className="relative flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex w-full flex-col gap-3 sm:max-w-lg sm:flex-row">
          <div className="relative w-full sm:w-64">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-lg leading-none text-gray-400">
              🔍
            </span>
            <input
              type="search"
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-sm transition-all duration-300 ease-in-out hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white "
              placeholder="Search"
              onChange={(e) => {
                e.preventDefault();
                debounceSearch(e.target.value);
              }}
            />
          </div>

          {filters?.length > 0 && (
            <FilterDropdown
              filters={filters}
              onFilterSelect={(selectedFilters) => {
                setSelectedFilters(selectedFilters);
                setPageIndex(0);
              }}
              finalFilters={selectedFilters}
            />
          )}
        </div>

        <ConfigMenu />
      </div>

      <div className="mt-4 h-full overflow-x-scroll xl:overflow-auto">
        {isLoading && tableData.length === 0 && (
          <div className="flex h-[400px] w-full items-center justify-center">
            <Loader />
          </div>
        )}
        {!isLoading &&
          (searchQuery || selectedFilterArray?.length > 0) &&
          filterTableData.length === 0 && (
            <span className="block w-full text-center"> No data found.</span>
          )}
        {!isLoading && tableData.length === 0 && (
          <span className="block w-full text-center"> No data found.</span>
        )}

        {((!!tableData.length &&
          !searchQuery &&
          !selectedFilterArray?.length) ||
          (!!filterTableData.length &&
            (searchQuery || selectedFilterArray?.length > 0))) && (
          <table {...getTableProps()} className="w-full">
            <thead>
              {headerGroups.map((headerGroup, index) => {
                return (
                  <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                    {headerGroup.headers.map((column, index) => {
                      return (
                        <th
                          key={index}
                          className="border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700"
                        >
                          <p className="w-20 text-xs tracking-wide text-gray-600">
                            {column.render("Header")}
                          </p>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell, index) => {
                      let data = cell.render("Cell");

                      if (cell.column.Header === "Status") {
                        data = (
                          <div className="mr-5 flex items-center gap-2">
                            <div className={`rounded-full text-xl`}>
                              {cell.value === true ? (
                                <MdCheckCircle className="text-green-500" />
                              ) : cell.value === false ? (
                                <MdCancel className="text-red-500" />
                              ) : cell.value === "Error" ? (
                                <MdOutlineError className="text-orange-500" />
                              ) : null}
                            </div>
                          </div>
                        );
                      }

                      if (cell.column.Header === "Created At") {
                        data = format(
                          new Date(cell?.value || null),
                          "yyyy-MM-dd HH:mm:ss"
                        );
                      }

                      if (cell.column.Header === "Actions") {
                        data = (
                          <Actions
                            width="w-[68px]"
                            value={cell.value}
                            cell={cell}
                          />
                        );
                      }

                      return (
                        <td
                          className="pt-[14px] pb-[8px] sm:text-[14px]"
                          {...cell.getCellProps()}
                          key={index}
                        >
                          <div className="scrollbar-custom mr-5">{data}</div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {((!!tableData.length && !searchQuery) ||
        (!!filterTableData.length && searchQuery)) && (
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => handlePageChange("previous")}
            disabled={pageIndex === 0}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
              pageIndex > 0
                ? "bg-blue-500 hover:bg-blue-600"
                : "cursor-not-allowed bg-gray-300"
            }`}
          >
            Previous
          </button>

          <span className="text-sm text-gray-700 dark:text-white">
            Page <strong>{pageIndex + 1}</strong> of{" "}
            <strong>{totalPages}</strong>
          </span>

          <button
            onClick={() => handlePageChange("next")}
            disabled={pageIndex >= totalPages - 1}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
              pageIndex < totalPages - 1
                ? "bg-blue-500 hover:bg-blue-600"
                : "cursor-not-allowed bg-gray-300"
            }`}
          >
            Next
          </button>
        </div>
      )}
    </Card>
  );
};

export default ConfigTable;
